import { useEffect, useMemo } from 'react'
import { useQuery, useReactiveVar } from '@apollo/client'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import { useLoading, useServerError } from 'shared/hooks'

import { userRolesVar } from 'modules/Users/store'
import { useUpdateUserMutation } from '../graphql/mutations'
import { useGetUserQuery } from '../graphql/queries'
import { formatRolesToOptions, formatUserData, userCampaignAdapter } from 'modules/Users/utils'

import { currentUserVar, notificationMessageVar } from 'modulesCommon/store'

import { GET_ROLES } from 'modulesCommon/graphql/queries'

import { IRole } from 'shared/interfaces'
import { TRouterParams } from 'shared/types'

import { userSchema } from '../constants/validation'

import { IUserForm } from '../types'

const useServiceUpdateUser = () => {
  const { id } = useParams<TRouterParams>()
  const navigate = useNavigate()

  const {
    data: userData,
    loading: isLoadingUser,
    error: userError
  } = useGetUserQuery({
    skip: isNaN(Number(id)),
    variables: { id: Number(id) }
  })

  const userRoles = useReactiveVar(userRolesVar)
  const loggedUser = useReactiveVar(currentUserVar)

  const methods = useForm<IUserForm>({
    defaultValues: {
      first_name: '',
      last_name: '',
      all_campaign: true,
      email: '',
    },
    resolver: yupResolver(userSchema(() => Promise.resolve(true))),
  })

  const [labelValue, checkboxValue] = methods.watch(['label_id', 'all_campaign'])

  const isExternalEmail = useMemo<boolean>(
    () => userData?.getUser?.is_external === true,
    [userData?.getUser?.is_external],
  )

  const isAllProjectsChecked = useMemo(() => checkboxValue || false, [checkboxValue])
  const userRolesOptions = useMemo(
    () => formatRolesToOptions(userRoles, isExternalEmail, loggedUser?.role?.role_id),
    [isExternalEmail, userRoles, loggedUser]
  )

  const { data: rolesData, loading: isRolesLoading, error: rolesError } = useQuery<{ getRoles: IRole[] }>(GET_ROLES)

  const [updateUser, { loading: isUpdateLoading, error: isUpdateError }] = useUpdateUserMutation()

  const onUpdateUser = async (values: IUserForm) => {
    if (typeof id === 'undefined') return
    const { data } = await updateUser({
      variables: { input: { ...formatUserData(values, isExternalEmail), id } }
    })

    if (data?.updateUser?.id) {
      navigate('/admin/users')
      notificationMessageVar({
        type: 'success',
        message: 'User successfully updated.',
        title: 'Success'
      })
    }
  }

  const isSubmitButtonDisabled = useMemo(() => isUpdateLoading, [isUpdateLoading])

  useEffect(() => {
    if (rolesData) userRolesVar(rolesData.getRoles)
  }, [rolesData])

  useEffect(() => {
    if (isUpdateError) {
      notificationMessageVar({
        type: 'warning',
        message: isUpdateError?.message,
        title: 'Warning'
      })
    }
  }, [isUpdateError])

  useEffect(() => {
    if (userData?.getUser) {
      const user = userData?.getUser
      methods.reset({
        first_name: user?.first_name ?? '',
        email: user?.email ?? '',
        last_name: user?.last_name ?? '',
        all_campaign: !user?.assigned_campaigns,
        label_id: typeof user.label_id === 'string' ? user.label_id : undefined,
        space_id: typeof user.space_id === 'string' ? user.space_id : undefined,
        country_code: user?.country_code || '',
        unit_id: user?.unit_id ? `${user?.unit_id}` : '',
        role: user?.role?.role_id || '',
        ...(user?.assigned_campaigns && { assigned_campaigns: user.assigned_campaigns.map(userCampaignAdapter) }),
        ...(user?.role?.expire_at && { expire_at: new Date(user?.role?.expire_at) })
      })
    }
  }, [userData?.getUser, methods])

  useEffect(() => {
    if (labelValue) {
      const unit = userData?.getUser?.unit_id && labelValue === userData?.getUser?.label_id ? `${userData?.getUser?.unit_id}` : ''
      methods.setValue('unit_id', unit)
    }
  }, [methods, labelValue, userData?.getUser])

  useLoading({
    isLoading:
      isLoadingUser || isRolesLoading || isUpdateLoading
  })
  useServerError([rolesError, userError])

  return {
    user: userData?.getUser,
    methods,
    control: methods.control,
    isExternalEmail,
    userRolesOptions,
    isAllProjectsChecked,
    handleSubmit: methods.handleSubmit,
    isSubmitButtonDisabled,
    onSubmit: onUpdateUser,
  }
}

export default useServiceUpdateUser
